 import { HttpClient } from "@angular/common/http";
 import { Injectable } from "@angular/core";
 import { IAPIListResult, IPagination} from "app/common/interfaces";
 import { AbstractService } from "app/services/abstract.service";
 import { environment as env } from "environments/environment.prod";
 import { Observable } from "rxjs";
 import { OrderAttributes, TripAttributes } from "@albatrosdeveloper/ave-models-package";
 import {
   andAllWhere,
   andWhere,
   buildQuery,
   FilterFn,
   noop,
   Ops,
   orderBy,
   OrderVariant,
   orWhere,
   page,
   where,
 } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
 import { GroupData, MapFilters, OrderMapFilter } from "app/shared/components/map-modal/models/map-modal.interface";
import { MapFilterTypeEnum } from "../models/map-modal.enum";
 
 const apiOrder = env.API_AVE_ORDER_URL;
 const moduleName = "order";
 
 @Injectable({
   providedIn: "root",
 })
 export class MapModalOrderService extends AbstractService<
   OrderAttributes,
   Partial<OrderAttributes>,
   Partial<OrderAttributes>
 > { 

  constructor(http: HttpClient) {
    super(apiOrder, moduleName, http);
  }

  private buildBaseQuery(
    options: IPagination<OrderAttributes>,
    document_type: string[],
    extraConditions: FilterFn<OrderAttributes>[] = []   // acá se pasan filtros adicionales
  ) {
    return buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andAllWhere('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      ...extraConditions,
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
  }

  /**
    * Pagination
    * Servicio para traer todos los clientes en monitor
    * @param options Pagination options
    * @returns Paginated data
    */
  findAllOrderGroupShipTo(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[],
    filter: {
      groupIds: string[]
      shipTo: string[],
      statusIds?: number[],
      isDirectSale?: boolean
    }
  ): Observable<IAPIListResult<GroupData>> {
    const extra: FilterFn<OrderAttributes>[] = [
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      filter.shipTo?.length ? andAllWhere("shipTo.id" as keyof OrderAttributes, Ops.in(...filter.shipTo)) : noop(),
      filter.statusIds && filter.statusIds.length > 0 ? andAllWhere("statusId" as keyof OrderAttributes,Ops.in(...filter.statusIds)): noop(),
      filter.isDirectSale ? andAllWhere("isDirectSale" as keyof OrderAttributes, Ops.eq(false)): noop(),
    ]

    const query = this.buildBaseQuery(options, document_type, extra);

    //const API_URL: string = apiOrder + `api/${order}/group/ship_to?filter=${encodeURIComponent(JSON.stringify(query))}`;
    const API_URL: string = apiOrder + `api/${moduleName}/group/ship_to`;
    return this.http.post<IAPIListResult<GroupData>>(API_URL, {
      filter: query,
      groupIds: filter.groupIds && filter.groupIds.length > 0 ? filter.groupIds : []
    });
  }

  findAllOrderGroupShipToWithExtraFilters(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[],
    filters: MapFilters
  ): Observable<IAPIListResult<GroupData>> {

    const filter = filters.filters
    const extraWhitoutTrip: FilterFn<OrderAttributes>[] = [
      andAllWhere("trip", Ops.eq(null)),
      filter.companyId ? andAllWhere("company.id" as keyof OrderAttributes, Ops.eq(filter.companyId)) : noop(),
      filter.statusIds?.length ? andAllWhere("statusId", Ops.in(...filter.statusIds)) : noop(),
      filter.documentClassId?.length ? andAllWhere("class.code" as keyof OrderAttributes, Ops.in(...filter.documentClassId)) : noop(),
      filter.salesOffice?.length ? andAllWhere("salesOffice.id" as keyof OrderAttributes, Ops.in(...filter.salesOffice)) : noop(),
      filter.route ? andAllWhere("routes.id" as keyof OrderAttributes, Ops.in(...[filter.route])): noop(),
      filter.subRouteId ? andAllWhere("subRoute.id" as keyof OrderAttributes, Ops.in([filter.subRouteId])) : noop(),
    ]

    const extra: FilterFn<OrderAttributes>[] = [
      andAllWhere("trip.dateHourEnd" as keyof OrderAttributes, Ops.not(null)),
      andAllWhere("operationType", Ops.not(4)),
      filter.companyId ? andAllWhere("company.id" as keyof OrderAttributes, Ops.eq(filter.companyId)) : noop(),
      filter.startDate && filter.endDate ? andAllWhere("loadDateTime", Ops.btwnDates(filter.startDate, filter.endDate)) : noop(),
      filter.statusIds?.length ? andAllWhere("statusId", Ops.in(...filter.statusIds)) : noop(),
      filter.documentClassId?.length ? andAllWhere("class.code" as keyof OrderAttributes, Ops.in(...filter.documentClassId)) : noop(),
      filter.salesOffice?.length ? andAllWhere("salesOffice.id" as keyof OrderAttributes, Ops.in(...filter.salesOffice)) : noop(),
      filter.route ? andAllWhere("routes.id" as keyof OrderAttributes, Ops.in(...[filter.route])): noop(),
      filter.subRouteId 
        ? andAllWhere("subRoute.id" as keyof OrderAttributes, Ops.in([filter.subRouteId])) 
        : andAllWhere("subRoute", Ops.not(null)), 
      filter.shipTo?.length ? andAllWhere("shipTo.id" as keyof OrderAttributes, Ops.in(...filter.shipTo)) : noop(),
    ];

    const baseExtra = filters.mode === MapFilterTypeEnum.EXTRA_WITHOUT_TRIP ? extraWhitoutTrip: extra;
    const query = buildQuery<OrderAttributes>(
      where('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      ...baseExtra,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiOrder + `api/${moduleName}/group/ship_to`;
    return this.http.post<IAPIListResult<GroupData>>(API_URL, {
      filter: query,
      groupIds: filter.groupIds && filter.groupIds.length > 0 ? filter.groupIds : []
    });
  }
}